import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, MessageSquare, Send, ShieldCheck, X, Zap } from "lucide-react";
import { api, UltradashDraft } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export function UltradashQueue() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = React.useState<number | null>(null);

  const summaryQ = useQuery({
    queryKey: ["ultradash", "queue", "summary"],
    queryFn: () => api.ultradashQueueSummary(),
    refetchInterval: 15_000,
  });
  const draftsQ = useQuery({
    queryKey: ["ultradash", "drafts", "pending"],
    queryFn: () => api.ultradashDrafts({ status: "pending_review", limit: 200 }),
    refetchInterval: 10_000,
  });

  // Auto-select first when none selected
  React.useEffect(() => {
    if (!selectedId && draftsQ.data?.items?.length) {
      setSelectedId(draftsQ.data.items[0].id);
    }
    if (selectedId && draftsQ.data?.items?.length) {
      const stillThere = draftsQ.data.items.some((d) => d.id === selectedId);
      if (!stillThere) setSelectedId(draftsQ.data.items[0].id);
    }
  }, [draftsQ.data, selectedId]);

  // Polling at 10s is enough; the realtime event is a custom one (not a table),
  // so we don't subscribe via useRealtime here.

  const totalPending = draftsQ.data?.items?.length ?? 0;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Ultradash Approval Queue</h1>
          <p className="text-sm text-muted-foreground">
            Kitt-drafted replies waiting for review. Each one is the AI impersonating a creator —
            review the inbound thread, then approve, edit, or reject.
          </p>
        </div>
        <BulkActions onDone={() => { draftsQ.refetch(); summaryQ.refetch(); }} />
      </header>

      <SummaryCards items={summaryQ.data?.items ?? []} loading={summaryQ.isLoading} />

      <div className="grid gap-4 md:grid-cols-[320px_1fr]">
        <div className="rounded-md border bg-card">
          <div className="flex items-center justify-between border-b px-3 py-1.5 text-xs">
            <span className="font-medium">Pending ({totalPending})</span>
          </div>
          {draftsQ.isLoading ? (
            <Skeleton className="m-3 h-40" />
          ) : !draftsQ.data?.items?.length ? (
            <Empty title="Nothing pending." hint="Drafts appear here as fans message in." />
          ) : (
            <ul className="max-h-[70vh] divide-y overflow-auto text-sm">
              {draftsQ.data.items.map((d) => (
                <li key={d.id}>
                  <button
                    onClick={() => setSelectedId(d.id)}
                    className={`w-full px-3 py-2 text-left hover:bg-accent/40 ${
                      d.id === selectedId ? "bg-accent" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium">
                        {d.creator_display_name || d.creator_id}
                      </span>
                      <span className="text-2xs text-muted-foreground">
                        {timeAgo(d.created_at)}
                      </span>
                    </div>
                    <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                      {d.draft_text}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DraftDetail
          draftId={selectedId}
          key={selectedId}
          onResolved={() => {
            draftsQ.refetch();
            summaryQ.refetch();
          }}
        />
      </div>
    </div>
  );
}

function SummaryCards({ items, loading }: { items: any[]; loading: boolean }) {
  if (loading) return <Skeleton className="h-20 w-full" />;
  if (!items.length) return null;
  return (
    <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-4">
      {items.map((row) => (
        <div key={row.creator_id} className="rounded-md border bg-card p-3">
          <div className="flex items-center justify-between">
            <span className="truncate text-sm font-medium">{row.creator_label}</span>
            <ApprovalModeBadge
              mode={row.ultradash_approval_mode}
              enabled={!!row.ai_chat_enabled}
              creator_id={row.creator_id}
            />
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1 text-2xs">
            <Stat label="pending" value={row.pending} hot={row.pending > 0} />
            <Stat label="sent 24h" value={row.sent_24h} />
            <Stat label="rejected 24h" value={row.rejected_24h} />
          </div>
          {!row.voice_clone_id && (
            <div className="mt-2 rounded bg-amber-50 px-2 py-1 text-2xs text-amber-700">
              ⚠ No voice clone — replies fall back to default voice.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, hot }: { label: string; value: number; hot?: boolean }) {
  return (
    <div className={`rounded px-1.5 py-1 ${hot ? "bg-amber-100 text-amber-900" : "bg-muted/40"}`}>
      <div className="text-2xs uppercase tracking-tight text-muted-foreground">{label}</div>
      <div className="font-semibold tnum">{value}</div>
    </div>
  );
}

function ApprovalModeBadge({
  mode, enabled, creator_id,
}: { mode: string | null; enabled: boolean; creator_id: string }) {
  const qc = useQueryClient();
  const next = mode === "auto" ? "review_required" : "auto";
  const m = useMutation({
    mutationFn: () => api.ultradashSetCreatorMode({ creator_id, mode: next as any }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ultradash", "queue", "summary"] }),
  });
  if (!enabled) {
    return (
      <span className="rounded bg-muted px-1.5 py-0.5 text-2xs text-muted-foreground">
        ai_chat off
      </span>
    );
  }
  return (
    <button
      onClick={() => m.mutate()}
      disabled={m.isPending}
      title={`Click to switch to ${next.replace("_", " ")}`}
      className={`rounded px-1.5 py-0.5 text-2xs ${
        mode === "auto"
          ? "bg-emerald-100 text-emerald-700"
          : "bg-blue-100 text-blue-700"
      }`}
    >
      {mode === "auto" ? <><Zap className="mr-0.5 inline h-2.5 w-2.5" />auto</> :
        <><ShieldCheck className="mr-0.5 inline h-2.5 w-2.5" />review</>}
    </button>
  );
}

function BulkActions({ onDone }: { onDone: () => void }) {
  const m = useMutation({
    mutationFn: () => api.ultradashDraftsBulkApprove(),
    onSuccess: onDone,
  });
  return (
    <Button
      onClick={() => {
        if (!confirm("Approve and send ALL pending Ultradash drafts? This cannot be undone.")) return;
        m.mutate();
      }}
      disabled={m.isPending}
      variant="outline"
      size="sm"
    >
      {m.isPending ? "Sending…" : "Bulk-approve all pending"}
    </Button>
  );
}

function DraftDetail({
  draftId, onResolved,
}: { draftId: number | null; onResolved: () => void }) {
  const detailQ = useQuery({
    queryKey: ["ultradash", "draft", draftId],
    queryFn: () => api.ultradashDraft(draftId!),
    enabled: !!draftId,
  });
  const [edited, setEdited] = React.useState<string>("");
  React.useEffect(() => {
    if (detailQ.data?.draft) setEdited(detailQ.data.draft.draft_text);
  }, [detailQ.data?.draft?.id]);

  const approveM = useMutation({
    mutationFn: () => api.ultradashDraftApprove(draftId!),
    onSuccess: onResolved,
  });
  const editSendM = useMutation({
    mutationFn: () => api.ultradashDraftEditSend(draftId!, edited),
    onSuccess: onResolved,
  });
  const rejectM = useMutation({
    mutationFn: (reason: string | undefined) => api.ultradashDraftReject(draftId!, reason),
    onSuccess: onResolved,
  });

  if (!draftId) {
    return (
      <div className="rounded-md border bg-card p-6">
        <Empty title="Pick a draft to review." />
      </div>
    );
  }
  if (detailQ.isLoading) return <Skeleton className="h-[60vh] w-full" />;
  if (!detailQ.data?.draft) return <Empty title="Draft not found." />;

  const d = detailQ.data.draft;
  const history = detailQ.data.history;
  const dirty = edited.trim() !== d.draft_text.trim();

  return (
    <div className="rounded-md border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-2 text-sm">
        <div className="space-y-0.5">
          <div className="font-medium">
            {d.creator_display_name || d.creator_id} → fan{" "}
            <span className="font-mono text-2xs text-muted-foreground">
              {(d.member_id || "").slice(0, 8)}
            </span>
          </div>
          <div className="text-2xs text-muted-foreground">
            club {d.club_id?.slice(0, 8)} · {d.model_used || "?"} ·{" "}
            {d.voice_clone_id ? `voice ${d.voice_clone_id.slice(0, 8)}` : "no voice clone"}
          </div>
        </div>
      </div>

      <div className="grid gap-0 md:grid-cols-2">
        <div className="border-r">
          <div className="border-b bg-muted/30 px-3 py-1 text-2xs uppercase text-muted-foreground">
            Recent thread
          </div>
          <div className="max-h-[55vh] space-y-2 overflow-auto p-3 text-sm">
            {history.length === 0 ? (
              <div className="text-xs text-muted-foreground">No prior context.</div>
            ) : (
              history.map((h, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] rounded-lg px-3 py-1.5 text-sm ${
                    h.direction === "outbound"
                      ? "ml-auto bg-blue-50 text-blue-900"
                      : "bg-muted"
                  }`}
                >
                  <div className="text-2xs uppercase text-muted-foreground">
                    {h.direction} · {h.composer}
                  </div>
                  <div className="whitespace-pre-wrap">{h.message_text}</div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="space-y-3 p-3">
          <div className="rounded-md bg-amber-50/40 px-3 py-2 text-2xs text-amber-900">
            <MessageSquare className="mr-1 inline h-3 w-3" />
            This is an AI-drafted reply impersonating the creator. Edit if needed before sending.
          </div>
          <Textarea
            value={edited}
            onChange={(e) => setEdited(e.target.value)}
            rows={6}
            className="font-medium"
          />
          <div className="flex flex-wrap gap-2">
            {!dirty ? (
              <Button
                onClick={() => approveM.mutate()}
                disabled={approveM.isPending}
                size="sm"
                className="gap-1.5"
              >
                <Check className="h-3.5 w-3.5" />
                {approveM.isPending ? "Sending…" : "Approve & send"}
              </Button>
            ) : (
              <Button
                onClick={() => editSendM.mutate()}
                disabled={editSendM.isPending}
                size="sm"
                className="gap-1.5"
              >
                <Send className="h-3.5 w-3.5" />
                {editSendM.isPending ? "Sending…" : "Save edits & send"}
              </Button>
            )}
            <Button
              onClick={() => {
                const reason = prompt("Reject reason (optional)") || undefined;
                rejectM.mutate(reason);
              }}
              disabled={rejectM.isPending}
              variant="outline"
              size="sm"
              className="gap-1.5"
            >
              <X className="h-3.5 w-3.5" /> Reject
            </Button>
          </div>
          {(approveM.error as any) && (
            <div className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
              {(approveM.error as Error).message}
            </div>
          )}
          {(editSendM.error as any) && (
            <div className="rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
              {(editSendM.error as Error).message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function timeAgo(ts: string): string {
  const t = new Date(ts).getTime();
  if (!t) return "";
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}
